import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Copy, Check, ChevronDown, Printer, BookText, NotebookPen, FileText, Route, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { buildStudyText, buildScriptureText, buildNotesText, buildJourneyText, buildAllSectionsText, printStudyDocument, printJourneyDocument } from "@/lib/studyText";

const PILL =
  "inline-flex items-center gap-2 rounded-full bg-[#0E131F] hover:bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20 pl-3 pr-4 py-2 text-sm font-medium transition-colors active:scale-95";

// Copy (with options) + Print/PDF actions for a study. Reused on the in-app study
// page and the public shared-study page. `includeNotes` shows the "My notes" option.
export default function StudyActions({
  study,
  passage,
  prayer = "",
  notes = "",
  answers = {},
  chat = [],
  includeNotes = false,
  testid = "study",
}) {
  const [copied, setCopied] = useState(false);

  // The full session arc is available when there's a conversation, notes, or saved answers.
  const hasJourney =
    (Array.isArray(chat) && chat.some((m) => (m?.content || "").trim())) ||
    (notes && notes.trim()) ||
    (answers && Object.keys(answers).some((k) => String(answers[k] || "").trim()));

  const doCopy = async (text, label) => {
    if (!text) { toast.info("You haven't written any notes yet."); return; }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} — paste it anywhere`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const copyWhole = () => doCopy(buildStudyText(study, passage, { prayer }), "Study copied");
  const copyAllSections = () => doCopy(buildAllSectionsText(study), "All teaching copied");
  const copyScripture = () => doCopy(buildScriptureText(study, passage), "Scripture copied");
  const copyNotes = () => doCopy(buildNotesText(study, { notes, answers }), "Notes copied");
  const copyJourney = () => doCopy(buildJourneyText(study, passage, { prayer, notes, answers, chat }), "Full journey copied");

  const print = () => {
    const ok = hasJourney
      ? printJourneyDocument(study, passage, { prayer, notes, answers, chat })
      : printStudyDocument(study, passage, { prayer });
    if (!ok) toast.error("Please allow pop-ups to print or save as PDF.");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`${testid}-copy-menu`} className={PILL}>
            {copied ? <Check className="w-4 h-4" strokeWidth={1.75} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
            {copied ? "Copied" : "Copy"}
            <ChevronDown className="w-3.5 h-3.5 -mr-1 opacity-70" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {hasJourney && (
            <DropdownMenuItem data-testid={`${testid}-copy-journey`} onClick={copyJourney} className="gap-2 cursor-pointer">
              <Route className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.6} /> Full journey (with Go Deeper)
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid={`${testid}-copy-whole`} onClick={copyWhole} className="gap-2 cursor-pointer">
            <FileText className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.6} /> Whole study
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testid}-copy-sections`} onClick={copyAllSections} className="gap-2 cursor-pointer">
            <ListChecks className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.6} /> All teaching sections
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testid}-copy-scripture`} onClick={copyScripture} className="gap-2 cursor-pointer">
            <BookText className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.6} /> Scripture only
          </DropdownMenuItem>
          {includeNotes && (
            <DropdownMenuItem data-testid={`${testid}-copy-notes`} onClick={copyNotes} className="gap-2 cursor-pointer">
              <NotebookPen className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.6} /> My notes
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <button data-testid={`${testid}-print`} onClick={print} className={PILL}>
        <Printer className="w-4 h-4" strokeWidth={1.75} /> Print / PDF
      </button>
    </>
  );
}
